// src/pages/Home.jsx
import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ProductCard from '../components/ProductCard';
import Slider from '../components/Slider';

const Home = () => {
  const { products, recommendedProductIds, user, openAuthModal } = useApp();
  const navigate = useNavigate();

  const latestProducts = [...products].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  ).slice(0, 8);
  
  const popularProducts = [...products].sort((a, b) => b.viewCount - a.viewCount).slice(0, 8);
  
  // ✅ 추천 제품 필터링
  const recommendedProducts = products.filter(p => recommendedProductIds.has(p.id)).slice(0, 8);

  const handleBecomeSeller = () => {
    if (user) {
      if (user.userType === 'seller') {
        navigate('/seller');
      } else {
        if (window.confirm('You are already registered as a buyer. Do you want to register as a seller as well?')) {
          navigate('/become-seller');
        }
      }
    } else {
      openAuthModal('seller', 'register');
    }
  };

  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-apple-bg">
      {/* Hero Section - Apple Store 스타일 */}
      <div className="pt-16 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="animate-fade-in">
          <h1 className="text-section-title-sm sm:text-section-title font-sf-pro text-apple-text-primary tracking-tight text-left max-w-3xl leading-tight">
            <span className="text-apple-accent">The finest B2B materials</span><br />
            For everyone around the world.
          </h1>
          <p className="text-body-sm text-apple-text-secondary mt-4 max-w-2xl leading-relaxed">
            Discover premium building materials and hardware from trusted suppliers worldwide. 
            Quality products, competitive pricing, reliable delivery.
          </p>
        </div>
      </div>

      {/* Latest Products - Apple Store 스타일 */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="animate-slide-up">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-subheading-sm sm:text-subheading font-sf-pro text-apple-text-primary tracking-tight">Latest Products</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/latest')}
                className="text-caption text-apple-accent hover:text-opacity-80 font-medium apple-hover"
              >
                View All →
              </button>
            </div>
          </div>
          <div className="relative">
            {latestProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Available</h3>
                  <p className="text-gray-600 mb-4">Check back later for new products or become a seller to add your own.</p>
                  <button
                    onClick={() => navigate('/become-seller')}
                    className="bg-apple-accent text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors"
                  >
                    Become a Seller
                  </button>
                </div>
              </div>
            ) : (
              <Slider>
                {latestProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </Slider>
            )}
          </div>
        </div>
      </section>

      {/* Popular Products - Apple Store 스타일 */}
      <section className="py-16 bg-apple-bg-secondary px-4 sm:px-6 lg:px-8">
        <div>
          <div className="animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-subheading-sm sm:text-subheading font-sf-pro text-apple-text-primary tracking-tight">Popular Products</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/popular')}
                  className="text-caption text-apple-accent hover:text-opacity-80 font-medium apple-hover"
                >
                  View All →
                </button>
              </div>
            </div>
            <div className="relative">
              {popularProducts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-gray-50 rounded-lg p-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Popular Products Yet</h3>
                    <p className="text-gray-600 mb-4">Products will appear here as they gain popularity.</p>
                  </div>
                </div>
              ) : (
                <Slider>
                  {popularProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </Slider>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Become a Seller - Apple Store 스타일 */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="animate-slide-up">
          <h2 className="text-subheading-sm sm:text-subheading font-sf-pro text-apple-text-primary tracking-tight mb-6 text-left">
            Become a Seller
          </h2>

          <div className="hidden sm:flex flex-wrap gap-4 mb-8">
            {[
              { title: "Export to Canada", description: "Start exporting to Canada with Namsan Bro.", backgroundImage: "/assets/icons/Image1.svg", icon: "/assets/icons/Icon1.svg" },
              { title: "Verified Buyers", description: "Connect with trusted B2B buyers.", backgroundImage: "/assets/icons/Image2.svg", icon: "/assets/icons/Icon2.svg" },
              { title: "MOQ Pricing", description: "Flexible pricing based on order volume.", backgroundImage: "/assets/icons/Image3.svg", icon: "/assets/icons/Icon3.svg" },
              { title: "Expert Support", description: "Get help from our export specialists.", backgroundImage: "/assets/icons/Image4.svg", icon: "/assets/icons/Icon4.svg" }
            ].map((item, index) => (
              <div key={index} className="w-[240px] flex-shrink-0 rounded-apple overflow-hidden aspect-[4/5] relative apple-hover">
                <img src={item.backgroundImage} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-apple-bg bg-opacity-70" />
                <div className="relative z-10 h-full flex flex-col justify-start items-start p-4">
                  <div className="mb-3">
                    <img src={item.icon} alt={item.title} className="w-6 h-6" />
                  </div>
                  <h3 className="text-body-sm font-sf-pro text-apple-text-primary mb-2">{item.title}</h3>
                  <p className="text-caption text-apple-text-secondary font-normal max-w-[90%] leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="sm:hidden mb-6">
            <div className="relative">
              <div 
                ref={scrollRef}
                className="flex gap-4 overflow-x-auto hide-scrollbar pb-2"
                style={{ scrollSnapType: 'x mandatory' }}
              >
                {[
                  { title: "Export to Canada", description: "Start exporting to Canada with Namsan Bro.", backgroundImage: "/assets/icons/Image1.svg", icon: "/assets/icons/Icon1.svg" },
                  { title: "Verified Buyers", description: "Connect with trusted B2B buyers.", backgroundImage: "/assets/icons/Image2.svg", icon: "/assets/icons/Icon2.svg" },
                  { title: "MOQ Pricing", description: "Flexible pricing based on order volume.", backgroundImage: "/assets/icons/Image3.svg", icon: "/assets/icons/Icon3.svg" },
                  { title: "Expert Support", description: "Get help from our export specialists.", backgroundImage: "/assets/icons/Image4.svg", icon: "/assets/icons/Icon4.svg" }
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="w-[200px] flex-shrink-0 rounded-apple overflow-hidden aspect-[4/5] relative snap-start apple-hover"
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <img src={item.backgroundImage} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-apple-bg bg-opacity-70" />
                    <div className="relative z-10 h-full flex flex-col justify-start items-start p-4">
                      <div className="mb-2">
                        <img src={item.icon} alt={item.title} className="w-5 h-5" />
                      </div>
                      <h3 className="text-caption font-sf-pro text-apple-text-primary mb-1">{item.title}</h3>
                      <p className="text-caption text-apple-text-secondary font-normal max-w-[90%] leading-relaxed text-xs">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full shadow-md z-20"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full shadow-md z-20"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="text-left">
          <button 
            onClick={handleBecomeSeller}
            className="inline-flex items-center text-sm font-medium text-apple-accent hover:text-opacity-80 transition-colors duration-200 apple-hover"
          > 
            Start Seller Registration
            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        </div>
      </section>

      {/* Recommended Products - Apple Store 스타일 */}
      <section className="py-16 bg-apple-bg-secondary px-4 sm:px-6 lg:px-8">
        <div>
          <div className="animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-subheading-sm sm:text-subheading font-sf-pro text-apple-text-primary tracking-tight">Recommended Products</h2>
              <button
                onClick={() => navigate('/recommended')}
                className="text-caption text-apple-accent hover:text-opacity-80 font-medium apple-hover"
              >
                View All →
              </button>
            </div>
            {recommendedProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Recommended Products</h3>
                  <p className="text-gray-600 mb-4">Personalized recommendations will appear here based on your preferences.</p>
                </div>
              </div>
            ) : (
              <Slider>
                {recommendedProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </Slider>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;